import { useCallback, useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, AddressElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Sparkle, ClipboardText, Calendar, CreditCard, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const PLAN_META = {
  base: { name: "Standard", tier: "base", yearly: false, monthly: "29€", full: "29€ / mois TTC", fullYearly: null, commitment: "sans engagement" },
  base_yearly: { name: "Standard", tier: "base", yearly: true, monthly: "26,10€", full: "26,10€ / mois TTC", fullYearly: "soit 313,20€/an TTC", commitment: "engagement annuel" },
  premium: { name: "Premium", tier: "premium", yearly: false, monthly: "49€", full: "49€ / mois TTC", fullYearly: null, commitment: "sans engagement" },
  premium_yearly: { name: "Premium", tier: "premium", yearly: true, monthly: "44,10€", full: "44,10€ / mois TTC", fullYearly: "soit 529,20€/an TTC", commitment: "engagement annuel" },
};

const TIER_LOST = [
  "Études statistiques anonymisées",
  "Comparaison de cohortes",
  "Analyse IA de vos compte-rendus",
  "Accès assistant de recherche clinique",
  "Exports CSV avancés",
];

const TIER_GAINED = TIER_LOST;

let _stripePromise = null;
async function getStripe() {
  if (_stripePromise) return _stripePromise;
  const { data } = await api.get("/billing/config");
  _stripePromise = loadStripe(data.publishable_key);
  return _stripePromise;
}

const ELEMENTS_APPEARANCE = {
  theme: "flat",
  variables: {
    colorPrimary: "#3A8FA5",
    colorBackground: "#FFFFFF",
    colorText: "#1E3A5F",
    colorTextSecondary: "#5F758F",
    colorDanger: "#B9502C",
    fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
    borderRadius: "6px",
    spacingUnit: "4px",
  },
  rules: {
    ".Label": { color: "#5F758F", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: "500" },
    ".Input": { border: "1px solid #E1E9F0", backgroundColor: "#FFFFFF", padding: "10px 12px" },
    ".Input:focus": { borderColor: "#3A8FA5", boxShadow: "0 0 0 1px #3A8FA5" },
    ".Tab": { border: "1px solid #E1E9F0", backgroundColor: "#F4F7FB" },
    ".Tab--selected": { borderColor: "#3A8FA5", backgroundColor: "#F9FBFA" },
  },
};

function CardStep({ onDone, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErrorMsg(null);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: window.location.origin + "/settings?tab=billing" },
    });
    if (error) { setErrorMsg(error.message || "Impossible d'enregistrer la carte"); setBusy(false); return; }
    if (!setupIntent || setupIntent.status !== "succeeded" || !setupIntent.payment_method) {
      setErrorMsg("Enregistrement en cours de vérification. Réessayez dans un instant.");
      setBusy(false); return;
    }
    try {
      await api.post("/billing/update-payment-method", { payment_method_id: setupIntent.payment_method });
      toast.success("Moyen de paiement mis à jour");
      onDone?.();
    } catch (err) {
      log.error("update payment method", err);
      setErrorMsg(err?.response?.data?.detail || "Enregistrement impossible");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Adresse de facturation</div>
        <AddressElement options={{ mode: "billing", display: { name: "organization" }, fields: { phone: "auto" } }} />
      </div>
      <div>
        <div className="text-xs tracking-widest uppercase text-[#5F758F] mb-3">Nouvelle carte bancaire</div>
        <PaymentElement options={{ layout: "tabs", wallets: { applePay: "auto", googlePay: "auto", link: "never" } }} />
      </div>
      {errorMsg && (
        <div className="text-sm text-[#B9502C] bg-[#FBE8E1] border border-[#B9502C]/20 rounded p-2">{errorMsg}</div>
      )}
      <p className="text-xs text-[#5F758F]">
        Paiement sécurisé — vos coordonnées bancaires ne transitent jamais par Questiomed.
      </p>
      <div className="flex justify-between gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onBack} disabled={busy} className="rounded-[10px] font-semibold" data-testid="change-plan-card-back">
          <ArrowLeft size={16} className="mr-1.5" /> Retour
        </Button>
        <Button type="submit" disabled={!stripe || busy} className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold transition-colors disabled:opacity-50" data-testid="change-plan-card-submit">
          <Lock size={16} className="mr-2" /> {busy ? "Enregistrement…" : "Enregistrer et revenir"}
        </Button>
      </div>
    </form>
  );
}

export default function ChangePlanDialog({ open, onOpenChange, currentPlan, targetPlan, defaultPaymentMethod, onCardUpdated, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [step, setStep] = useState("confirm"); // 'confirm' | 'update-card'
  const [stripeObj, setStripeObj] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const from = PLAN_META[currentPlan];
  const to = PLAN_META[targetPlan];

  useEffect(() => {
    if (!open || !targetPlan) { setPreview(null); setTerms(false); setStep("confirm"); return; }
    setPreviewLoading(true);
    api.post("/billing/preview-change-plan", { target_plan: targetPlan })
      .then(({ data }) => setPreview(data))
      .catch((err) => { log.warn("preview change plan", err); setPreview(null); })
      .finally(() => setPreviewLoading(false));
  }, [open, targetPlan]);

  const openCardStep = useCallback(async () => {
    setCardLoading(true);
    setStep("update-card");
    try {
      const s = await getStripe();
      setStripeObj(s);
      const { data } = await api.post("/billing/setup-intent", {});
      setClientSecret(data.client_secret);
    } catch (err) {
      log.error("setup intent init", err);
      toast.error(err?.response?.data?.detail || "Impossible d'initialiser le formulaire carte");
      setStep("confirm");
    } finally { setCardLoading(false); }
  }, []);

  const cardDone = useCallback(() => {
    setClientSecret(null);
    setStep("confirm");
    onCardUpdated?.();
  }, [onCardUpdated]);

  const kind = useMemo(() => {
    if (!from || !to) return null;
    const tierChange = from.tier !== to.tier;
    const intervalChange = from.yearly !== to.yearly;
    if (tierChange && to.tier === "premium") return "upgrade_tier";
    if (tierChange && to.tier === "base") return "downgrade_tier";
    if (intervalChange && to.yearly) return "to_yearly";
    if (intervalChange && !to.yearly) return "to_monthly";
    return "noop";
  }, [from, to]);

  if (!from || !to || kind === null) return null;

  const config = {
    upgrade_tier: {
      icon: Sparkle, iconClass: "text-[#3A8FA5]",
      confirmClass: "bg-[#3A8FA5] hover:bg-[#164E5F]",
      changes: [
        { label: "Accès aux fonctionnalités Premium", subItems: TIER_GAINED },
        to.yearly ? "Engagement de 12 mois" : "Sans engagement",
        to.yearly ? "Résiliation possible à la fin de l'engagement" : "Résiliation possible à tout moment",
        "Différence de tarif au prorata prélevée immédiatement",
      ],
    },
    downgrade_tier: {
      icon: ClipboardText, iconClass: "text-[#3A8FA5]",
      confirmClass: "bg-[#1E3A5F] hover:bg-[#0F2340]",
      changes: [
        { label: "Perte de l'accès aux fonctionnalités Premium", subItems: TIER_LOST },
        to.yearly ? "Engagement de 12 mois" : "Sans engagement",
        to.yearly ? "Résiliation possible à la fin de l'engagement" : "Résiliation possible à tout moment",
        "Crédit des jours Premium non consommés déduit de la prochaine facture",
      ],
    },
    to_yearly: {
      icon: Calendar, iconClass: "text-[#3A8FA5]",
      confirmClass: "bg-[#3A8FA5] hover:bg-[#164E5F]",
      changes: [
        `Nouveau prélèvement mensuel : ${to.monthly} TTC (−10 %)`,
        `Économie annuelle : ${from.tier === "premium" ? "58,80€" : "34,80€"}`,
        "Engagement de 12 mois",
        "Résiliation possible à la fin de l'engagement",
        "Différence tarifaire au prorata sur la prochaine facture",
      ],
    },
    to_monthly: {
      icon: Calendar, iconClass: "text-[#5F758F]",
      confirmClass: "bg-[#1E3A5F] hover:bg-[#0F2340]",
      changes: [
        `Nouveau prélèvement mensuel : ${to.monthly} TTC`,
        `Perte de la réduction annuelle de 10 % (${from.tier === "premium" ? "58,80€" : "34,80€"} / an)`,
        "Sans engagement",
        "Résiliation possible à tout moment",
        "Ajustement au prorata sur la prochaine facture",
      ],
    },
    noop: null,
  }[kind];

  if (!config) return null;
  const Icon = config.icon;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/billing/upgrade", { target_plan: targetPlan });
      toast.success("Formule mise à jour");
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      log.error("change plan", err);
      toast.error(err?.response?.data?.detail || "Changement de formule impossible");
    } finally { setBusy(false); }
  };

  const inCardStep = step === "update-card";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F] flex items-center gap-2">
            {inCardStep
              ? <>Modifier le moyen de paiement</>
              : <>Changer d'abonnement</>}
          </DialogTitle>
          {inCardStep && (
            <DialogDescription className="text-sm text-[#5F758F]">
              Enregistrez une nouvelle carte, elle sera utilisée pour le prélèvement du prorata et les prochains prélèvements mensuels.
            </DialogDescription>
          )}
        </DialogHeader>

        {inCardStep ? (
          cardLoading || !stripeObj || !clientSecret ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex gap-2">
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" />
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 rounded-full bg-[#3A8FA5] dot" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          ) : (
            <Elements stripe={stripeObj} options={{ clientSecret, appearance: ELEMENTS_APPEARANCE }}>
              <CardStep onDone={cardDone} onBack={() => setStep("confirm")} />
            </Elements>
          )
        ) : (
          <>
            <div className="rounded-[14px] p-5 shadow-[0_2px_10px_rgba(30,58,95,0.06)] bg-white border border-[#C7DEE6]" data-testid="change-plan-compare">
              <div className="flex items-stretch justify-between gap-2">
                <div className="flex-1 text-center py-1 opacity-70">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#5F758F] font-bold">Actuellement</p>
                  <p className="text-base font-bold text-[#1E3A5F] mt-1.5">{from.name}</p>
                  <p className="text-xs text-[#5F758F] mt-0.5">{from.commitment}</p>
                  <p className="text-sm text-[#1E3A5F] font-bold mt-1 tabular-nums">{from.full}</p>
                  {from.fullYearly && (
                    <p className="text-[11px] text-[#5F758F] mt-0.5 tabular-nums">{from.fullYearly}</p>
                  )}
                </div>
                <div className="flex flex-col items-center justify-center shrink-0 px-2">
                  <div className="w-8 h-8 rounded-full bg-[#3A8FA5] flex items-center justify-center shadow-[0_2px_6px_rgba(58,143,165,0.35)]">
                    <ArrowRight size={16} weight="bold" className="text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center py-2 rounded-[10px] bg-[#E6F1F5] ring-1 ring-[#3A8FA5]/30">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#3A8FA5] font-bold">Nouvelle formule</p>
                  <p className="text-base font-bold text-[#1E3A5F] mt-1.5">{to.name}</p>
                  <p className="text-xs text-[#3A8FA5] font-medium mt-0.5">{to.commitment}</p>
                  <p className="text-sm text-[#1E3A5F] font-bold mt-1 tabular-nums">{to.full}</p>
                  {to.fullYearly && (
                    <p className="text-[11px] text-[#3A8FA5] mt-0.5 tabular-nums">{to.fullYearly}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-[10px] p-4 bg-[#F4F7FB] border border-[#E1E9F0]" data-testid="change-plan-changes">
              <ul className="space-y-1.5 text-xs text-[#1E3A5F]">
                {config.changes.map((item, idx) => {
                  if (typeof item === "string") {
                    return (
                      <li key={idx} className="flex items-start gap-1.5">
                        <span className="text-[#5F758F] mt-0.5">•</span><span>{item}</span>
                      </li>
                    );
                  }
                  return (
                    <li key={idx}>
                      <div className="flex items-start gap-1.5">
                        <span className="text-[#5F758F] mt-0.5">•</span>
                        <span className="font-semibold">{item.label}</span>
                      </div>
                      <ul className="ml-5 mt-1 space-y-1 text-[11px] text-[#3A5A70]">
                        {item.subItems.map((s) => (
                          <li key={s} className="flex items-start gap-1.5">
                            <span className="text-[#B7C4D6] mt-0.5">◦</span><span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="rounded-[10px] p-3 flex items-center justify-between gap-3 bg-[#F4F7FB] border border-[#E1E9F0]" data-testid="change-plan-preview">
              <div className="text-xs uppercase tracking-[0.14em] text-[#5F758F] font-medium">
                {preview?.kind === "credit" ? "Crédit sur prochaine facture" : preview?.kind === "charge" ? "Prélèvement immédiat au prorata" : "Ajustement au prorata"}
              </div>
              <div className="text-lg font-bold text-[#1E3A5F] tabular-nums">
                {previewLoading ? "…" : preview
                  ? `${(Math.abs(preview.amount) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € TTC`
                  : "—"}
              </div>
            </div>

            {preview?.kind === "charge" && (
              <div className="text-xs text-[#5F758F] leading-relaxed bg-white border border-[#E1E9F0] rounded-md p-3 flex items-center justify-between gap-3" data-testid="change-plan-card-info">
                <div className="flex items-center gap-2 min-w-0">
                  <CreditCard size={16} weight="duotone" className="text-[#3A8FA5] shrink-0" />
                  <span className="truncate">
                    {defaultPaymentMethod?.last4
                      ? <>Prélèvement sur votre carte <strong className="uppercase">{defaultPaymentMethod.brand}</strong> <strong>•••• {defaultPaymentMethod.last4}</strong></>
                      : "Aucune carte enregistrée"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={openCardStep}
                  disabled={busy}
                  className="text-xs font-semibold text-[#3A8FA5] hover:text-[#164E5F] underline underline-offset-2 whitespace-nowrap transition-colors"
                  data-testid="change-plan-switch-card"
                >
                  {defaultPaymentMethod?.last4 ? "Changer de carte" : "Ajouter une carte"}
                </button>
              </div>
            )}

            <label className="flex items-start gap-2.5 text-xs text-[#5F758F] leading-relaxed cursor-pointer">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[#C7DEE6] text-[#3A8FA5] focus:ring-[#3A8FA5]"
                data-testid="change-plan-terms"
              />
              <span>
                J'accepte les{" "}
                <a href="/cgv" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGV</a>,{" "}
                les <a href="/cgu" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">CGU</a>{" "}
                et la <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline hover:text-[#1E3A5F]">politique de confidentialité</a> de Questiomed.
              </span>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-[10px] font-semibold" data-testid="change-plan-abort-btn">
                Annuler
              </Button>
              <Button
                onClick={submit}
                disabled={busy || !terms}
                className={`rounded-[10px] text-white font-semibold transition-colors disabled:opacity-50 ${config.confirmClass}`}
                data-testid="change-plan-confirm-btn"
              >
                {busy
                  ? "Traitement…"
                  : preview?.kind === "charge"
                  ? "Confirmer et payer"
                  : "Confirmer"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
